import React, { useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Grid, Card, CardHeader, List, ListItem, ListItemText, ListItemIcon, Checkbox, Button, Divider, TextField, InputAdornment } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import _ from 'lodash';
import { IsEmpty } from '../../../common/checks';

const useStyles = makeStyles((theme) => ({
  root: {
    margin: 'auto',
  },
  cardHeader: {
    padding: theme.spacing(1, 2),
  },
  list: {
    width:           300,
    height:          230,
    backgroundColor: theme.palette.background.paper,
    overflow:        'auto',
  },
  button: {
    margin: theme.spacing(0.5, 0),
  },
}));

function not(a, b) {
  return a.filter((value) => b.indexOf(value) === -1);
}

function intersection(a, b) {
  return a.filter((value) => b.indexOf(value) !== -1);
}

function union(a, b) {
  return [...a, ...not(b, a)];
}

const TransferList = ({ page, postId, onChangePromoIsNationwide, handleAddPromoLocations, promoLocations, itemText }) => {
  const classes = useStyles();
  const [custom, setCustom] = React.useState([]);
  const [checked, setChecked] = React.useState([]);
  const [left, setLeft] = React.useState([]);
  const [leftCopy, setLeftCopy] = React.useState([]);
  const [right, setRight] = React.useState([]);
  const [rightCopy, setRightCopy] = React.useState([]);
  const [searchValue, setSearchValue] = React.useState('');

  const leftChecked = intersection(checked, left);
  const rightChecked = intersection(checked, right);

  useEffect(() => {
    if (page) {
      const filteredArr = [];
      let availableLocation;
      const set = new Set();
      const filteredOutletPromoLocations = _.map(promoLocations, (location) => _.assign({
        outletId:   location.outletId,
        name:       location.name,
        address:    location.address,
        postalCode: location.postalCode,
        remarks:    location.remarks,
        lat:        location.lat,
        lng:        location.lng,
        locatedIn:  !IsEmpty(location.locatedIn) ? location.locatedIn : '',
        lookUp:     `${!IsEmpty(location.locatedIn) ? location.locatedIn : ''} ${location.address}`,
      }));

      filteredOutletPromoLocations.forEach((location) => { set.add(location.postalCode.concat(location.remarks)); });

      page.locations.forEach((location) => {
        const locatedIn = (location.locatedIn) ? location.locatedIn : '';
        availableLocation = {
          postId,
          outletId:   location.id,
          name:       location.name,
          address:    location.address,
          lookUp:     `${locatedIn}, ${location.address}`,
          postalCode: location.postalCode,
          remarks:    location.unitNo,
          lat:        location.lat,
          lng:        location.lng,
          locatedIn:  !IsEmpty(location.locatedIn) ? location.locatedIn : '',
        };

        if (!_.some(filteredOutletPromoLocations, { outletId: availableLocation.outletId })) {
          filteredArr.push(availableLocation);
        }
      });
      setLeft(filteredArr);
      setLeftCopy(filteredArr);

      /* Hide Custom Locations (w/o OutletIds) in TransferList */
      const outletPromoLocations = filteredOutletPromoLocations.filter((location) => location.outletId !== null && location.outletId !== '');
      setRight(outletPromoLocations);
      setRightCopy(outletPromoLocations);

      const customPromoLocations = _.difference(filteredOutletPromoLocations, outletPromoLocations);
      setCustom(customPromoLocations);
    }
  }, []);

  useEffect(() => {
    if (left.length < 1) {
      onChangePromoIsNationwide(true);
    } else {
      onChangePromoIsNationwide(false);
    }
  }, [left]);

  useEffect(() => {
    /* Add Back Custom Locations (w/o OutletIds) bef. passing to Parent Component */
    handleAddPromoLocations(right.concat(custom));
  }, [right]);

  const handleToggle = (value) => () => {
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }
    setChecked(newChecked);
  };

  const numberOfChecked = (items) => intersection(checked, items).length;

  const handleToggleAll = (items) => () => {
    if (numberOfChecked(items) === items.length) {
      setChecked(not(checked, items));
    } else {
      setChecked(union(checked, items));
    }
  };

  const handleCheckedRight = () => {
    setSearchValue('');
    setRight(rightCopy.concat(leftChecked));
    setRightCopy(rightCopy.concat(leftChecked));
    setLeft(not(leftCopy, leftChecked));
    setLeftCopy(not(leftCopy, leftChecked));
    setChecked(not(checked, leftChecked));
  };

  const handleCheckedLeft = () => {
    setSearchValue('');
    setLeft(leftCopy.concat(rightChecked));
    setLeftCopy(leftCopy.concat(rightChecked));
    setRight(not(rightCopy, rightChecked));
    setRightCopy(not(rightCopy, rightChecked));
    setChecked(not(checked, rightChecked));
  };

  const customList = (title, items) => (
    <Card>
      <CardHeader
        className={classes.cardHeader}
        avatar={(
          <Checkbox
            color="primary"
            onClick={handleToggleAll(items)}
            checked={numberOfChecked(items) === items.length && items.length !== 0}
            indeterminate={numberOfChecked(items) !== items.length && numberOfChecked(items) !== 0}
            disabled={items.length === 0}
            inputProps={{ 'aria-label': 'All Items Selected' }}
          />
        )}
        title={title}
        subheader={`${numberOfChecked(items)}/${items.length} Selected`}
      />
      <Divider />
      <List className={classes.list} dense component="div" role="list">
        {items.map((value) => {
          const labelId = `transfer-list-all-item-${value}-label`;
          return (
            <ListItem key={`${value.outletId}-${value.locatedIn}-${value.address}-${value.remarks}`} role="listitem" button onClick={handleToggle(value)}>
              <ListItemIcon>
                <Checkbox
                  color="primary"
                  checked={checked.indexOf(value) !== -1}
                  tabIndex={-1}
                  disableRipple
                  inputProps={{ 'aria-labelledby': labelId }}
                />
              </ListItemIcon>
              <ListItemText
                id={labelId}
                primary={(
                  <div>
                    {!IsEmpty(value.name) && (
                    <>
                      {value.name}
                      {' '}
                      <br />
                    </>
                    )}
                    {value.locatedIn}
                    <br />
                    {value.address}
                    <br />
                    {value.remarks}
                  </div>
                )}
              />
            </ListItem>
          );
        })}
      </List>
    </Card>
  );

  const onChangeSearch = (e) => {
    setSearchValue(e.target.value);
    if (e.target.value) {
      const leftArr = _.filter(leftCopy, (outlet) => outlet.lookUp.toLowerCase().includes(e.target.value.toLowerCase()) || outlet.remarks.toLowerCase().includes(e.target.value.toLowerCase()) || outlet.name.toLowerCase().includes(e.target.value.toLowerCase()));
      setLeft(leftArr);

      const rightArr = _.filter(rightCopy, (outlet) => outlet.lookUp.toLowerCase().includes(e.target.value.toLowerCase()) || outlet.remarks.toLowerCase().includes(e.target.value.toLowerCase()) || outlet.name.toLowerCase().includes(e.target.value.toLowerCase()));
      setRight(rightArr);
    } else {
      setLeft(leftCopy);
      setRight(rightCopy);
    }
  };

  return (
    <div>
      <TextField
        id="input-with-icon-textfield"
        placeholder="Search"
        autoComplete="off"
        onChange={(e) => onChangeSearch(e)}
        value={searchValue}
        style={{ marginTop: 10, marginBottom: 10 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />
      <Grid container justify="center" alignItems="center" className={classes.root}>
        <Grid item lg={5} md={12}>{customList(`Available ${itemText || 'Outlet'}(s)`, left)}</Grid>
        <Grid item lg={2} md={12}>
          <Grid container direction="column" alignItems="center">
            <Button
              variant="outlined"
              size="small"
              className={classes.button}
              onClick={handleCheckedRight}
              disabled={leftChecked.length === 0}
              aria-label="move selected right"
            >
              &gt;
            </Button>
            <Button
              variant="outlined"
              size="small"
              className={classes.button}
              onClick={handleCheckedLeft}
              disabled={rightChecked.length === 0}
              aria-label="move selected left"
            >
              &lt;
            </Button>
          </Grid>
        </Grid>
        <Grid item lg={5} md={12}>{customList(`${itemText || 'Outlet'}(s) With Promotion`, right)}</Grid>
      </Grid>
    </div>
  );
};

export default TransferList;
